/**
 * usePipeline (CHAP-246) — fetch the donor pipeline + edit a record.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '../lib/api.js';
import type { PipelineResponse, PipelineStage, CapacityTier } from '../../shared/pipeline.js';

export function usePipeline(slug: string) {
  return useQuery<PipelineResponse, Error>({
    queryKey: ['pipeline', slug],
    queryFn: () => apiGet<PipelineResponse>(`/api/c/${slug}/pipeline`),
    enabled: !!slug,
  });
}

export interface PipelinePatch {
  pipelineStage?: PipelineStage;
  capacityTier?: CapacityTier;
  pipelinePledgedAmount?: number;
}

export function useUpdatePipeline(slug: string) {
  const qc = useQueryClient();
  return useMutation<{ autoQualifySuggested?: boolean }, Error, { id: string } & PipelinePatch>({
    mutationFn: ({ id, ...body }) => apiPatch(`/api/c/${slug}/pipeline/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline', slug] }),
  });
}

export interface BulkTierResult {
  ok: boolean;
  updatedCount: number;
  autoQualifiedCount: number;
}

export function useBulkTierPipeline(slug: string) {
  const qc = useQueryClient();
  return useMutation<BulkTierResult, Error, { recordIds: string[]; capacityTier: CapacityTier }>({
    mutationFn: (body) => apiPost(`/api/c/${slug}/pipeline/bulk-tier`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline', slug] }),
  });
}
